import React from 'react';
import {View, Text, FlatList, StyleSheet} from 'react-native';
import {Commentary, COMMENTARY_ICONS, CommentaryType} from '../../types/diretta';
import {Colors} from '../../theme/colors';
import {useTheme} from '../../hooks/useTheme';

interface CommentaryTabProps {
  commentaries: Commentary[];
  loading?: boolean;
}

export const CommentaryTab: React.FC<CommentaryTabProps> = ({commentaries, loading}) => {
  const {colors, isDark} = useTheme();
  const getIcon = (type: CommentaryType): string => {
    return COMMENTARY_ICONS[type] || '💬';
  };

  const isSpecialMarker = (time: string): 'halftime' | 'fulltime' | 'et-halftime' | 'et-fulltime' | null => {
    const clean = (time || '').replace(/[''′]/g, '').trim().toUpperCase();
    // First half end
    if (clean === 'HT' || clean === '45+' || clean === 'INTERVALLO') return 'halftime';
    // Second half end / Full time
    if (clean === 'FT' || clean === '90+' || clean === 'FINE') return 'fulltime';
    // Extra time first half end
    if (clean === '105+') return 'et-halftime';
    // Extra time second half end
    if (clean === '120+') return 'et-fulltime';
    return null;
  };

  const renderCommentary = ({item}: {item: Commentary}) => {
    const marker = isSpecialMarker(item.comment_time || '');
    const hasTime = item.comment_time && item.comment_time !== '0';

    // Halftime marker
    if (marker === 'halftime') {
      return (
        <View>
          <View style={styles.markerRow}>
            <Text style={styles.markerText}>⏱️ Fine Primo Tempo</Text>
          </View>
          {item.comment_text && (
            <View style={[styles.noTimeRow, {backgroundColor: isDark ? colors.surface : '#faf8fc'}]}>
              <Text style={[styles.commentText, {color: colors.textPrimary}]}>{item.comment_text}</Text>
            </View>
          )}
        </View>
      );
    }

    // Fulltime marker
    if (marker === 'fulltime') {
      return (
        <View>
          <View style={[styles.markerRow, styles.fulltimeMarker]}>
            <Text style={styles.markerText}>Fine Partita</Text>
          </View>
          {item.comment_text && (
            <View style={[styles.noTimeRow, {backgroundColor: isDark ? colors.surface : '#faf8fc'}]}>
              <Text style={[styles.commentText, {color: colors.textPrimary}]}>{item.comment_text}</Text>
            </View>
          )}
        </View>
      );
    }

    // Extra time halftime marker (105+)
    if (marker === 'et-halftime') {
      return (
        <View>
          <View style={[styles.markerRow, styles.etHalftimeMarker]}>
            <Text style={styles.markerText}>Fine Primo Tempo Supplementare</Text>
          </View>
          {item.comment_text && (
            <View style={[styles.noTimeRow, {backgroundColor: isDark ? colors.surface : '#faf8fc'}]}>
              <Text style={[styles.commentText, {color: colors.textPrimary}]}>{item.comment_text}</Text>
            </View>
          )}
        </View>
      );
    }

    // Extra time fulltime marker (120+)
    if (marker === 'et-fulltime') {
      return (
        <View>
          <View style={[styles.markerRow, styles.fulltimeMarker]}>
            <Text style={styles.markerText}>Fine Tempi Supplementari</Text>
          </View>
          {item.comment_text && (
            <View style={[styles.noTimeRow, {backgroundColor: isDark ? colors.surface : '#faf8fc'}]}>
              <Text style={[styles.commentText, {color: colors.textPrimary}]}>{item.comment_text}</Text>
            </View>
          )}
        </View>
      );
    }

    // No time (pre-match comment)
    if (!hasTime) {
      return (
        <View style={[styles.noTimeRow, {backgroundColor: isDark ? colors.surface : '#faf8fc'}]}>
          <Text style={[styles.commentText, styles.italicText, {color: isDark ? colors.textSecondary : '#666'}]}>
            {item.comment_text}
          </Text>
        </View>
      );
    }

    // Regular row
    return (
      <View style={[
        styles.row,
        {backgroundColor: colors.surface, borderBottomColor: colors.border},
        item.is_important && [styles.importantRow, {backgroundColor: isDark ? 'rgba(107, 70, 193, 0.15)' : '#f3eef8'}],
      ]}>
        <View style={styles.timeColumn}>
          <Text style={styles.iconText}>
            {getIcon(item.comment_class)}
          </Text>
          <Text style={[styles.timeText, {color: colors.primary}]}>{item.comment_time}</Text>
        </View>
        <Text style={[
          styles.commentText,
          {color: colors.textPrimary},
          item.is_bold && styles.boldText,
        ]}>
          {item.comment_text}
        </Text>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>Caricamento...</Text>
      </View>
    );
  }

  if (!commentaries || commentaries.length === 0) {
    return (
      <View style={[styles.emptyContainer, {backgroundColor: isDark ? colors.surface : '#f3eefc', borderColor: isDark ? colors.border : '#d4c5ec'}]}>
        <Text style={styles.emptyTitle}>Ciao tifosi viola!</Text>
        <Text style={[styles.emptyText, {color: isDark ? colors.textSecondary : '#5a1894'}]}>
          La partita deve ancora entrare nel vivo, quindi per ora non ci sono commenti in diretta.{'\n'}
          Torna qui al fischio d'inizio per seguirla azione per azione.
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      data={commentaries}
      renderItem={renderCommentary}
      keyExtractor={(item) => item.id.toString()}
      contentContainerStyle={styles.list}
      showsVerticalScrollIndicator={false}
      nestedScrollEnabled
      scrollEnabled={false}
    />
  );
};

const styles = StyleSheet.create({
  list: {
    paddingBottom: 100,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.06)',
  },
  importantRow: {
    backgroundColor: '#f3eef8',
    borderLeftWidth: 4,
    borderLeftColor: Colors.primary,
    paddingLeft: 12,
  },
  noTimeRow: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#faf8fc',
  },
  markerRow: {
    backgroundColor: Colors.primary,
    paddingVertical: 12,
    alignItems: 'center',
  },
  fulltimeMarker: {
    backgroundColor: '#1a1a1a',
  },
  etHalftimeMarker: {
    backgroundColor: '#ff6b35',
  },
  markerText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  timeColumn: {
    width: 56,
    alignItems: 'center',
    marginRight: 12,
  },
  timeText: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.primary,
    textAlign: 'center',
  },
  iconText: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 4,
  },
  commentText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
  },
  boldText: {
    fontWeight: '600',
  },
  italicText: {
    fontStyle: 'italic',
  },
  emptyContainer: {
    padding: 30,
    alignItems: 'center',
    backgroundColor: '#f3eefc',
    margin: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#d4c5ec',
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.primary,
    marginBottom: 10,
  },
  emptyText: {
    fontSize: 15,
    color: '#5a1894',
    textAlign: 'center',
    lineHeight: 24,
  },
});
